/**
 * Creates ol3 layers from layers defined in configuration
 * @module configuration-features
 * @see {@link http://wiki.infor.com:8080/confluence/label/~CALIX/layer-factory|blog}
 */

import ol = require("ol");
import MapView = require("../views/MapView");
import Portal = require("portal-gis");
import Utils = require("../utils");
import SymbologyUtils = require("../utils/symbology");
import Converter = require("../utils/ol3/converter");
import EventHandler = require("./base/event-handler");
import AgsLayers = require("../utils/ags/ags-layers");

const log = Utils.log;

let styleCache = <ol.style.Style[]>[];

class Handler {

    static about = `
Creates a [vector layer](http://openlayers.org/en/latest/apidoc/ol.layer.Vector.html) from layers defined in configuration.

Usage Example:

    <LAYER id="civics" text="Search Results" type="app/layer-factory/configuration-features">
    <COMMANDS>
        <COMMAND about="Adds a 'more info' button to the info viewer popup" disabled="False" id="more-info" mid="app/commands/trigger" text="View Details" type="action">
        <OPTIONS>
            <Values id="event" value="civics:show-details" />
            <Values id="css-name" value="btn btn-secondary" />
        </OPTIONS>
        </COMMAND>
    </COMMANDS>
    <OPTIONS>
        <Values about="List the fields in the order you'd like them to appear in the infoViewer" id="field-list" value="id" />
        <Values about="The vertical offset of the markers associated with this layer, a 1 will shift the marker up so the bottom edge touches the point-feature; use 0.5 to center." id="anchorYValue" value="1" />
        <Values id="show-labels" value="false" />
        <Values id="layerType" value="cluster" />
        <Values id="cluster-distance" value="20" />
    </OPTIONS>
    </LAYER>

Triggering _clear-features-from-layer_ will clear all features from this layer.

`;

    static options = {
        'layerType': {
            "domain": 'standard|cluster',
            "default": "standard",
        },
        'cluster-distance': {
            "about": 'ol.source.Cluster "distance" parameter (minimum distance in pixels between clusters).',
            "default": "20",
        },
        'cluster-symbology': {
            "about": 'symbology to use for clustering',
        },
        'symbology': {
            "about": 'symbology to use for clustering when "cluster-symbology" not provided',
        },
    };

    private static cluster(l: Portal.MapLayerLeaf, view: MapView) {
        let mediator = view.mediator;
        let layer = <ol.layer.Vector>Handler.standard(l, view);
        let source = layer.getSource();
        let layerOptions = Utils.asDictionary(l.Options.Values);

        let clusterSource = new ol.source.Cluster({
            // polygons render as points but at a certain zoom level we need to disable clustering and render them as polygons
            geometryFunction: (feature: ol.Feature) => {
                let g = feature.getGeometry();
                if (g instanceof ol.geom.Polygon) {
                    g = g.getInteriorPoint();
                }
                return g;
            },
            distance: parseInt(layerOptions["cluster-distance"] || "40", 10),
            source: source
        });

        // replace cluster creation to support rendering the original feature when it is all alone (will only work in debug version of ol3)
        clusterSource.createCluster_ = function (features) {
            if (1 === features.length) return features[0];
            var centroid = [0, 0];
            for (var i = features.length - 1; i >= 0; --i) {
                var geometry = this.geometryFunction_(features[i]);
                if (geometry) {
                    ol.coordinate.add(centroid, geometry.getCoordinates());
                } else {
                    features.splice(i, 1);
                }
            }
            ol.coordinate.scale(centroid, 1 / features.length);

            var cluster = new ol.Feature(new ol.geom.Point(centroid));
            cluster.set('features', features);
            return cluster;
        };

        layer.setSource(clusterSource);


        let style = (feature: ol.Feature, resolution: number) => {
            let features = <ol.Feature[]>feature.get('features');
            let selectedFeatures = features.filter(f => !!f.get("attributes")["selected"]);

            let internalIcon = features.map((obj=>obj.get("attributes").internalIcon));
            let internalLabel = features.map((obj=>obj.get("attributes").internalLabel));
            let internalSymbology = features.map((obj=>obj.get("attributes").internalSymbology));

            return SymbologyUtils.findStyle(mediator, <any>{
                attributes: {
                    count: features.length,
                    selected: selectedFeatures.length,
                    internalIcon: internalIcon,
                    internalLabel: internalLabel,
					internalSymbology: internalSymbology[0],
                    symbology: layerOptions["cluster-symbology"] || layerOptions["symbology"]
                }
            });
        };


        layer.setStyle(style);

        return layer;
    }

    private static standard(l: Portal.MapLayerLeaf, view: MapView): ol.layer.Layer {

        let layer = l;
        let mediator = view.mediator;

        let vectorSource = new ol.source.Vector({
            features: []
        });

        let vectorLayer = new ol.layer.Vector({
            id: layer ? layer.id : "feature-layer",
            source: vectorSource
        });
        //HPS-118519 - setting the z-index of symbology layer to be always on top
        vectorLayer.setZIndex(Infinity);
        
        EventHandler.register({
            config: layer,
            view: view,
            source: vectorSource,
            layer: vectorLayer
        });

        Utils.extend(vectorLayer, { config: layer });

        if (layer) {
            let converter = new Converter();

            let mapView = view;

            let renderFeatures = (layer: Portal.MapLayerLeaf) => {
                // clear the ol3 source because we're about to re-render all the features in the configuration layer
                vectorSource.clear(true);

                // make symbology option an extension
                let layerOptions = Utils.asDictionary(layer.Options.Values);
                let layerSymbology = layerOptions["symbology"];

                layer.Features.Features.forEach(f => {
                    let options = Utils.asDictionary(f.Options.Values);
                    let featureSymbology = options["symbology"] || layerSymbology;
                    featureSymbology && Utils.extend(f, { symbology: featureSymbology });
                });

                let olFeatures = SymbologyUtils.copyToMap(converter.fromConfigLayer(layer).features, mapView, vectorSource, layerOptions);
                // the infoviewer needs to access the original configuration for feature-level commands
                olFeatures.forEach((f, i) => Utils.extend(f, {
                    config: layer.Features.Features[i]
                }));
            };
            renderFeatures(layer);

            mediator.on("clear-features-from-layer", (args: {
                in?: any;
                layer?: string;
            }) => {
                let layerName = args.in && args.in.findOption("layer-name") || args.layer;
                if (!layerName || 0 <= Utils.split(layerName).indexOf(layer.id)) {
                    layer.Features.Features = [];
                    vectorSource.clear();
                }
            });

            // watch the layer for changes, ideally would merge but for now only render features (assumes they were already cleared)
            let watchLayer = mediator.api.layers.get(layer.id);
            if (watchLayer) {
                watchLayer.on("change", () => renderFeatures(watchLayer.attributes));
            }

        }

        return vectorLayer;

    }

    static create(args: { layer: Portal.MapLayerLeaf; view: MapView }): ol.layer.Layer {
        var l = args.layer;
        var options = Utils.asDictionary(l.Options.Values);
        var layerType = options["layerType"] || Handler.options.layerType.default;
        let factory: typeof Handler.standard = Handler[layerType];

        if (!factory) throw `invalid layerType: ${layerType}`;

        let layer = factory && factory(l, args.view);
        AgsLayers.setMinMaxZoomLevel(args.view, layer, l);
        return layer;
    }

}

export = Handler; 